import axios from 'axios';

const getMasterApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3001/api/master';
  }
  return process.env.REACT_APP_MASTER_API_URL || '/api/master';
};

const masterApiClient = axios.create({
  baseURL: getMasterApiBaseUrl(),
  timeout: 60000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

masterApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('masterToken') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

masterApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('masterToken');
      window.location.href = '/master/login';
    }
    return Promise.reject(error);
  }
);

export const masterAuth = {
  login: async (email, password) => {
    try {
      const response = await masterApiClient.post('/login', {
        email,
        password,
      });
      if (response.data.data?.token) {
        localStorage.setItem('masterToken', response.data.data.token);
      }
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || error.message 
      };
    }
  },

  logout: async () => {
    try {
      await masterApiClient.post('/logout');
      localStorage.removeItem('masterToken');
      return { success: true };
    } catch (error) {
      localStorage.removeItem('masterToken');
      return { success: true };
    }
  },

  getProfile: async (userId) => {
    try {
      const response = await masterApiClient.get(`/user/${userId}/profile`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  updateProfile: async (userId, profileData) => {
    try {
      const response = await masterApiClient.put(`/user/${userId}/profile`, profileData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  changePassword: async (userId, passwordData) => {
    try {
      const response = await masterApiClient.put(`/user/${userId}/password`, passwordData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  validateInviteCode: async (inviteCode) => {
    try {
      const response = await masterApiClient.post('/invite/validate', {
        code: inviteCode,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  signup: async (signupData) => {
    try {
      const response = await masterApiClient.post('/signup', signupData);
      if (response.data.data?.token) {
        localStorage.setItem('masterToken', response.data.data.token);
      }
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || error.message 
      };
    }
  },
};

export const masterDashboard = {
  getSummary: async (masterId) => {
    try {
      const response = await masterApiClient.get(`/${masterId}/summary`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  getStats: async (masterId) => {
    try {
      const response = await masterApiClient.get(`/${masterId}/stats`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  getRecentActivities: async (masterId, limit = 10) => {
    try {
      const response = await masterApiClient.get(`/${masterId}/activities`, {
        params: { limit },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },
};

export const masterScenarios = {
  getAll: async () => {
    try {
      const response = await masterApiClient.get('/scenarios');
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  getById: async (scenarioId) => {
    try {
      const response = await masterApiClient.get(`/scenarios/${scenarioId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  createInvoice: async (scenarioId, invoiceData) => {
    try {
      const response = await masterApiClient.post(`/scenarios/${scenarioId}/invoice`, invoiceData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },
};

export const masterInvoices = {
  getAll: async (masterId, filters = {}) => {
    try {
      const response = await masterApiClient.get(`/${masterId}/invoices`, {
        params: filters,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  getById: async (invoiceId) => {
    try {
      const response = await masterApiClient.get(`/invoices/${invoiceId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  syncWithFBR: async (invoiceId) => {
    try {
      const response = await masterApiClient.post(`/invoices/${invoiceId}/sync-fbr`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  resubmit: async (invoiceId) => {
    try {
      const response = await masterApiClient.post(`/invoices/${invoiceId}/resubmit`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  search: async (masterId, searchTerm) => {
    try {
      const response = await masterApiClient.get(`/${masterId}/invoices/search`, {
        params: { q: searchTerm },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },
};

export const masterUsers = {
  getAll: async (masterId, filters = {}) => {
    try {
      const response = await masterApiClient.get(`/${masterId}/users`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  getById: async (userId) => {
    try {
      const response = await masterApiClient.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  create: async (masterId, userData) => {
    try {
      const response = await masterApiClient.post(`/${masterId}/users`, userData);
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  update: async (userId, userData) => {
    try {
      const response = await masterApiClient.put(`/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  delete: async (userId) => {
    try {
      const response = await masterApiClient.delete(`/users/${userId}`);
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  toggleStatus: async (userId, status) => {
    try {
      const response = await masterApiClient.patch(`/users/${userId}/status`, { status });
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  getActivityHistory: async (userId) => {
    try {
      const response = await masterApiClient.get(`/users/${userId}/activity`);
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },
};

export const masterAdmin = {
  listMasters: async () => {
    try {
      const response = await masterApiClient.get('/admin/masters');
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  createMaster: async (masterData) => {
    try {
      const response = await masterApiClient.post('/admin/masters', masterData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  getMaster: async (masterId) => {
    try {
      const response = await masterApiClient.get(`/admin/masters/${masterId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  updateMaster: async (masterId, masterData) => {
    try {
      const response = await masterApiClient.put(`/admin/masters/${masterId}`, masterData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  deleteMaster: async (masterId) => {
    try {
      const response = await masterApiClient.delete(`/admin/masters/${masterId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  generateInviteCode: async (masterId, inviteData) => {
    try {
      const response = await masterApiClient.post(`/admin/masters/${masterId}/invite-codes`, inviteData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  listInviteCodes: async (masterId) => {
    try {
      const response = await masterApiClient.get(`/admin/masters/${masterId}/invite-codes`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  getAllInviteCodes: async () => {
    try {
      const response = await masterApiClient.get('/admin/masters/invite-codes');
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  revokeInviteCode: async (codeId) => {
    try {
      const response = await masterApiClient.post(`/admin/masters/invite-codes/${codeId}/revoke`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },
};

const masterApi = {
  auth: masterAuth,
  dashboard: masterDashboard,
  scenarios: masterScenarios,
  invoices: masterInvoices,
  users: masterUsers,
  admin: masterAdmin,
};

export default masterApi;
